/**
 * EntityManager: CRUD, bulk operations, schema management, relations.
 */

import { db } from './connection';
import { SQLBuilder } from './sql-builder';
import { Compression } from './compression';
import { QueryBuilder } from './query-builder';
import type {
  EntitySchema,
  ColumnDefinition,
  ColumnType,
  QueryOptions,
  WhereClause,
  BulkInsertOptions,
  BulkUpdateOptions,
  MigrationAction,
  RelationDefinition,
} from './types';

export class EntityManager<T extends Record<string, unknown>> {
  private _schema: EntitySchema<T>;
  private _columnMap: Map<string, ColumnDefinition>;
  private _compressedColumns: Set<string>;

  constructor(schema: EntitySchema<T>) {
    this._schema = schema;
    this._columnMap = new Map(schema.columns.map((c) => [c.name, c]));
    this._compressedColumns = new Set(schema.compressedColumns ?? []);
    schema.columns
      .filter((c) => c.type === 'COMPRESSED_JSON')
      .forEach((c) => this._compressedColumns.add(c.name));
  }

  get tableName(): string {
    return this._schema.tableName;
  }

  get schema(): EntitySchema<T> {
    return this._schema;
  }

  async createTable(): Promise<void> {
    const sql = SQLBuilder.createTable(this._schema);
    await db.execute(sql);
    const indexSqls = SQLBuilder.createIndexes(this._schema);
    for (const indexSql of indexSqls) {
      await db.execute(indexSql);
    }
  }

  async dropTable(): Promise<void> {
    const sql = SQLBuilder.dropTable(this._schema.tableName);
    await db.execute(sql);
  }

  async alterTable(action: MigrationAction): Promise<void> {
    const sql = SQLBuilder.alterTable(this._schema.tableName, action);
    await db.execute(sql);
  }

  async tableExists(): Promise<boolean> {
    const result = await db.query<{ name: string }>(
      `SELECT name FROM sqlite_master WHERE type='table' AND name=?`,
      [this._schema.tableName]
    );
    return result.length > 0;
  }

  async addIndex(name: string, columns: string[], unique = false): Promise<void> {
    const sql = SQLBuilder.createIndex(this._schema.tableName, { name, columns, unique });
    await db.execute(sql);
  }

  async dropIndex(name: string): Promise<void> {
    const sql = SQLBuilder.dropIndex(name);
    await db.execute(sql);
  }

  async insert(entity: Partial<T>): Promise<T> {
    const { columns, values } = this._prepareInsert(entity);
    const sql = SQLBuilder.insert(this._schema.tableName, columns);
    const result = await db.run(sql, values);
    const pkColumn = this._schema.columns.find((c) => c.primary);
    if (pkColumn && pkColumn.autoIncrement && result.lastId) {
      (entity as Record<string, unknown>)[pkColumn.name] = result.lastId;
    }
    return entity as T;
  }

  async insertOrReplace(entity: Partial<T>): Promise<T> {
    const { columns, values } = this._prepareInsert(entity);
    const sql = SQLBuilder.insert(this._schema.tableName, columns, true, false);
    await db.run(sql, values);
    return entity as T;
  }

  async insertOrIgnore(entity: Partial<T>): Promise<T> {
    const { columns, values } = this._prepareInsert(entity);
    const sql = SQLBuilder.insert(this._schema.tableName, columns, false, true);
    await db.run(sql, values);
    return entity as T;
  }

  async update(entity: Partial<T>, where: WhereClause): Promise<number> {
    const { columns, values } = this._prepareUpdate(entity);
    const whereClause = SQLBuilder.buildWhere(where);
    const sql = SQLBuilder.update(this._schema.tableName, columns, whereClause.sql);
    const allValues = [...values, ...whereClause.params];
    const result = await db.run(sql, allValues);
    return result.changes;
  }

  async delete(where: WhereClause): Promise<number> {
    const whereClause = SQLBuilder.buildWhere(where);
    const sql = SQLBuilder.delete(this._schema.tableName, whereClause.sql);
    const result = await db.run(sql, whereClause.params);
    return result.changes;
  }

  async findOne(options: QueryOptions = {}): Promise<T | null> {
    const results = await this.find({ ...options, limit: 1 });
    return results[0] ?? null;
  }

  async findById(id: string | number): Promise<T | null> {
    const pkColumn = this._schema.columns.find((c) => c.primary);
    if (!pkColumn) throw new Error('No primary key defined');
    return this.findOne({ where: { [pkColumn.name]: id } as WhereClause });
  }

  async find(options: QueryOptions = {}): Promise<T[]> {
    const whereClause = options.where
      ? SQLBuilder.buildWhere(options.where)
      : { sql: '1=1', params: [] };
    const sql = SQLBuilder.select(this._schema.tableName, {
      columns: options.select,
      where: whereClause.sql,
      orderBy: options.orderBy,
      limit: options.limit,
      offset: options.offset,
    });
    const rows = await db.query<Record<string, unknown>>(sql, whereClause.params);
    const entities = rows.map((row) => this._hydrateEntity(row));
    if (options.relations?.length) {
      await this._loadRelations(entities, options.relations);
    }
    return entities;
  }

  async findAll(): Promise<T[]> {
    return this.find();
  }

  async count(where?: WhereClause): Promise<number> {
    const whereClause = where ? SQLBuilder.buildWhere(where) : { sql: '1=1', params: [] };
    const sql = SQLBuilder.count(this._schema.tableName, whereClause.sql);
    const result = await db.query<{ count: number }>(sql, whereClause.params);
    return result[0]?.count ?? 0;
  }

  async exists(where: WhereClause): Promise<boolean> {
    const count = await this.count(where);
    return count > 0;
  }

  async bulkInsert(entities: Partial<T>[], options: BulkInsertOptions = {}): Promise<void> {
    if (entities.length === 0) return;
    const chunkSize = options.chunkSize ?? 100;
    const chunks = this._chunk(entities, chunkSize);
    await db.transaction(async () => {
      for (const chunk of chunks) {
        await this._insertChunk(chunk, options);
      }
    });
  }

  async bulkUpdate(
    entities: Array<{ where: WhereClause; data: Partial<T> }>,
    options: BulkUpdateOptions = {}
  ): Promise<number> {
    if (entities.length === 0) return 0;
    const chunkSize = options.chunkSize ?? 100;
    const chunks = this._chunk(entities, chunkSize);
    let totalChanges = 0;
    await db.transaction(async () => {
      for (const chunk of chunks) {
        for (const item of chunk) {
          const changes = await this.update(item.data, item.where);
          totalChanges += changes;
        }
      }
    });
    return totalChanges;
  }

  async bulkDelete(whereConditions: WhereClause[]): Promise<number> {
    if (whereConditions.length === 0) return 0;
    let totalChanges = 0;
    await db.transaction(async () => {
      for (const where of whereConditions) {
        const changes = await this.delete(where);
        totalChanges += changes;
      }
    });
    return totalChanges;
  }

  async bulkUpsert(entities: Partial<T>[], conflictColumns: string[]): Promise<void> {
    if (entities.length === 0) return;
    const first = entities[0];
    if (!first) return;
    const columns = this._getInsertColumns(first);
    const updateColumns = columns.filter((c) => !conflictColumns.includes(c));
    await db.transaction(async () => {
      for (const entity of entities) {
        const { values } = this._prepareInsert(entity);
        const conflictClause = conflictColumns.join(', ');
        const updateClause = updateColumns.map((c) => `${c} = excluded.${c}`).join(', ');
        const sql = `INSERT INTO ${this._schema.tableName} (${columns.join(', ')})
          VALUES (${columns.map(() => '?').join(', ')})
          ON CONFLICT(${conflictClause}) DO UPDATE SET ${updateClause}`;
        await db.run(sql, values);
      }
    });
  }

  query(): QueryBuilder<T> {
    return new QueryBuilder<T>(this);
  }

  private _prepareInsert(entity: Partial<T>): { columns: string[]; values: unknown[] } {
    const columns: string[] = [];
    const values: unknown[] = [];
    for (const col of this._schema.columns) {
      if (col.autoIncrement && entity[col.name as keyof T] === undefined) continue;
      const value = entity[col.name as keyof T];
      if (value !== undefined) {
        columns.push(col.name);
        values.push(this._serializeValue(col.name, value, col.type));
      }
    }
    return { columns, values };
  }

  private _prepareUpdate(entity: Partial<T>): { columns: string[]; values: unknown[] } {
    const columns: string[] = [];
    const values: unknown[] = [];
    for (const [key, value] of Object.entries(entity)) {
      const col = this._columnMap.get(key);
      if (!col || col.primary) continue;
      columns.push(key);
      values.push(this._serializeValue(key, value, col.type));
    }
    return { columns, values };
  }

  private _getInsertColumns(entity: Partial<T>): string[] {
    return this._schema.columns
      .filter((col) => !col.autoIncrement || entity[col.name as keyof T] !== undefined)
      .filter((col) => entity[col.name as keyof T] !== undefined)
      .map((col) => col.name);
  }

  private async _insertChunk(entities: Partial<T>[], options: BulkInsertOptions): Promise<void> {
    const statements = entities.map((entity) => {
      const { columns, values } = this._prepareInsert(entity);
      return {
        statement: SQLBuilder.insert(
          this._schema.tableName,
          columns,
          options.orReplace,
          options.orIgnore
        ),
        values,
      };
    });
    await db.executeSet(statements);
  }

  private _serializeValue(columnName: string, value: unknown, type: ColumnType): unknown {
    if (value === null || value === undefined) return null;
    if (type === 'JSON') return JSON.stringify(value);
    if (type === 'COMPRESSED_JSON' || this._compressedColumns.has(columnName)) {
      return Compression.compress(value);
    }
    if (type === 'BOOLEAN') return value ? 1 : 0;
    return value;
  }

  private _deserializeValue(columnName: string, value: unknown, type: ColumnType): unknown {
    if (value === null || value === undefined) return null;
    if (type === 'JSON') return typeof value === 'string' ? JSON.parse(value) : value;
    if (type === 'COMPRESSED_JSON' || this._compressedColumns.has(columnName)) {
      return Compression.decompress(value as string);
    }
    if (type === 'BOOLEAN') return value === 1 || value === true;
    return value;
  }

  private _hydrateEntity(row: Record<string, unknown>): T {
    const entity: Record<string, unknown> = {};
    for (const col of this._schema.columns) {
      if (row[col.name] !== undefined) {
        entity[col.name] = this._deserializeValue(col.name, row[col.name], col.type);
      }
    }
    return entity as T;
  }

  private async _loadRelations(entities: T[], relationNames: string[]): Promise<void> {
    if (!this._schema.relations) return;
    for (const relName of relationNames) {
      const relation = this._schema.relations[relName];
      if (!relation) continue;
      switch (relation.type) {
        case 'ManyToOne':
        case 'OneToOne':
          await this._loadToOneRelation(entities, relName, relation);
          break;
        case 'OneToMany':
          await this._loadToManyRelation(entities, relName, relation);
          break;
      }
    }
  }

  private async _loadToOneRelation(
    entities: T[],
    relName: string,
    relation: RelationDefinition
  ): Promise<void> {
    const targetSchema = relation.target();
    const targetManager = new EntityManager(targetSchema);
    const fkValues = entities
      .map((e) => (e as Record<string, unknown>)[relation.foreignKey])
      .filter((v) => v != null);
    if (fkValues.length === 0) return;
    const pkColumn = targetSchema.columns.find((c) => c.primary)!;
    const related = await targetManager.find({
      where: { [pkColumn.name]: fkValues } as WhereClause,
    });
    const relatedMap = new Map(related.map((r) => [(r as Record<string, unknown>)[pkColumn.name], r]));
    for (const entity of entities) {
      const fkValue = (entity as Record<string, unknown>)[relation.foreignKey];
      (entity as Record<string, unknown>)[relName] = relatedMap.get(fkValue) ?? null;
    }
  }

  private async _loadToManyRelation(
    entities: T[],
    relName: string,
    relation: RelationDefinition
  ): Promise<void> {
    const targetSchema = relation.target();
    const targetManager = new EntityManager(targetSchema);
    const pkColumn = this._schema.columns.find((c) => c.primary)!;
    const pkValues = entities.map((e) => (e as Record<string, unknown>)[pkColumn.name]);
    if (pkValues.length === 0) return;
    const related = await targetManager.find({
      where: { [relation.foreignKey]: pkValues } as WhereClause,
    });
    const relatedMap = new Map<unknown, unknown[]>();
    for (const r of related) {
      const fkValue = (r as Record<string, unknown>)[relation.foreignKey];
      if (!relatedMap.has(fkValue)) relatedMap.set(fkValue, []);
      relatedMap.get(fkValue)!.push(r);
    }
    for (const entity of entities) {
      const pkValue = (entity as Record<string, unknown>)[pkColumn.name];
      (entity as Record<string, unknown>)[relName] = relatedMap.get(pkValue) ?? [];
    }
  }

  private _chunk<U>(array: U[], size: number): U[][] {
    const chunks: U[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }
}
